// CalDAV (RFC 4791) client. Speaks WebDAV/CalDAV against the portal API's
// authenticated pass-through at /api/caldav/, which fronts the calendar service.
import api from "../api";

const ACCOUNT_ROOT = "/api/caldav/";

const DAV_NS = "DAV:";
const CALDAV_NS = "urn:ietf:params:xml:ns:caldav";

const davRequest = async (method, url, { body, headers = {} } = {}) => {
  const response = await api.request({
    method,
    url,
    data: body,
    headers,
    bypassInterceptor: true,
    responseType: "text",
    // Axios lower-cases custom methods; WebDAV servers expect them upper-case.
    transformRequest: [(data) => data],
    validateStatus: (status) => status >= 200 && status < 300,
  });
  return response;
};

const parseXml = (text) => {
  const doc = new DOMParser().parseFromString(text, "application/xml");
  if (doc.querySelector("parsererror")) {
    throw new Error("Invalid XML in CalDAV response");
  }
  return doc;
};

const firstNS = (node, ns, localName) =>
  node.getElementsByTagNameNS(ns, localName)[0] ?? null;

const textNS = (node, ns, localName) =>
  firstNS(node, ns, localName)?.textContent?.trim() ?? null;

// Multistatus responses repeat <D:response> blocks; map each to href + props.
const parseMultistatus = (text) => {
  const doc = parseXml(text);
  return Array.from(doc.getElementsByTagNameNS(DAV_NS, "response")).map((res) => ({
    href: textNS(res, DAV_NS, "href"),
    element: res,
  }));
};

const propfind = async (url, propsXml, depth = 0) =>
  davRequest("PROPFIND", url, {
    body:
      `<?xml version="1.0" encoding="utf-8"?>` +
      `<D:propfind xmlns:D="DAV:" xmlns:C="urn:ietf:params:xml:ns:caldav" xmlns:CS="http://calendarserver.org/ns/">` +
      `<D:prop>${propsXml}</D:prop>` +
      `</D:propfind>`,
    headers: {
      Depth: String(depth),
      "Content-Type": "application/xml; charset=utf-8",
    },
  });

/**
 * RFC 4791 account discovery: account root -> current-user-principal ->
 * calendar-home-set -> calendar collections.
 *
 * `accountRoot` defaults to the caller's own calendar. Pass /api/caldav/u/{userId}/ to discover a
 * colleague's shared calendar — the server rewrites every returned href onto that same root, so
 * follow-up REPORT/PUT/DELETE stay scoped to the right calendar.
 */
export const discoverCalendars = async (accountRoot = ACCOUNT_ROOT) => {
  const rootRes = await propfind(accountRoot, "<D:current-user-principal/>", 0);
  const rootProps = parseMultistatus(rootRes.data)[0];
  const principalHref =
    rootProps && textNS(rootProps.element, DAV_NS, "current-user-principal")
      ? textNS(firstNS(rootProps.element, DAV_NS, "current-user-principal"), DAV_NS, "href")
      : null;
  if (!principalHref) throw new Error("CalDAV discovery failed: no principal");

  const principalRes = await propfind(principalHref, "<C:calendar-home-set/>", 0);
  const principalProps = parseMultistatus(principalRes.data)[0];
  const homeHref = principalProps
    ? textNS(firstNS(principalProps.element, CALDAV_NS, "calendar-home-set"), DAV_NS, "href")
    : null;
  if (!homeHref) throw new Error("CalDAV discovery failed: no calendar home");

  const homeRes = await propfind(
    homeHref,
    "<D:resourcetype/><D:displayname/><CS:getctag/>",
    1,
  );

  return parseMultistatus(homeRes.data)
    .filter((res) => {
      const resourceType = firstNS(res.element, DAV_NS, "resourcetype");
      return resourceType && firstNS(resourceType, CALDAV_NS, "calendar");
    })
    .map((res) => ({
      href: res.href,
      displayName: textNS(res.element, DAV_NS, "displayname") || "Calendar",
      ctag: textNS(res.element, "http://calendarserver.org/ns/", "getctag"),
    }));
};

/** Collection tag — changes whenever any item in the calendar changes. */
export const getCalendarCtag = async (calendarHref) => {
  const res = await propfind(calendarHref, "<CS:getctag/>", 0);
  const props = parseMultistatus(res.data)[0];
  return props ? textNS(props.element, "http://calendarserver.org/ns/", "getctag") : null;
};

/**
 * REPORT calendar-query: fetches every object in the collection with its
 * calendar-data and etag. Returns [{ href, etag, data }].
 */
export const fetchCalendarObjects = async (calendarHref) => {
  const res = await davRequest("REPORT", calendarHref, {
    body:
      `<?xml version="1.0" encoding="utf-8"?>` +
      `<C:calendar-query xmlns:D="DAV:" xmlns:C="urn:ietf:params:xml:ns:caldav">` +
      `<D:prop><D:getetag/><C:calendar-data/></D:prop>` +
      `<C:filter><C:comp-filter name="VCALENDAR"/></C:filter>` +
      `</C:calendar-query>`,
    headers: {
      Depth: "1",
      "Content-Type": "application/xml; charset=utf-8",
    },
  });

  return parseMultistatus(res.data)
    .map((r) => ({
      href: r.href,
      etag: textNS(r.element, DAV_NS, "getetag"),
      data: textNS(r.element, CALDAV_NS, "calendar-data"),
    }))
    .filter((r) => r.data);
};

/**
 * Creates or updates a calendar object. Pass the current etag when updating so
 * the server can reject lost updates (412). Returns the new etag if provided.
 */
export const putCalendarObject = async (href, ics, etag = null) => {
  const headers = { "Content-Type": "text/calendar; charset=utf-8" };
  if (etag) headers["If-Match"] = etag;
  const res = await davRequest("PUT", href, { body: ics, headers });
  return res.headers?.etag ?? null;
};

export const deleteCalendarObject = async (href, etag = null) => {
  const headers = {};
  if (etag) headers["If-Match"] = etag;
  await davRequest("DELETE", href, { headers });
};

/** Builds the resource href for a new object inside a calendar collection. */
export const objectHref = (calendarHref, uid) =>
  `${calendarHref.replace(/\/$/, "")}/${encodeURIComponent(uid)}.ics`;

export const newUid = () =>
  (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`) +
  "@ontik.com";
